/**
 * Common - Cart management.
 */

// Imports
import GlobalVars from '../common/GlobalVars';
import {
  isEmpty,
  type,
} from './Utils';
import {
  readCookie,
  createCookie,
  deleteCookie,
} from './tools/Cookie';
import {
  triggerCartItemAddedEvent,
  triggerCartItemRemovedEvent,
  cartItemAddedEventName,
  triggerRemoveFromCartEvent,
} from './Events';
// Popin
import { addToBagPopin } from './organism/popin.addtobag';

const _ = {};
_.map = require('lodash/map');
_.clone = require('lodash/clone');
_.includes = require('lodash/includes');

// Constants (ids)
const cartItemsCountId = 'cartItemsCount';

// Constants (classes)
const noDisplayClass = 'u-no-display';

/**
 * List of SKUs for all the products in bag.
 * @type {Array}
 */
let skusList = [];

/**
 * Reference to the cart "items count" container element.
 * @type {Object}
 */
let cartItemsCountContainer = {};

function saveReferences() {
  // Save reference to the cart "items count" container element
  cartItemsCountContainer = $(`#${cartItemsCountId}`);
}

function getCookieName(sku) {
  return `ecomBag${GlobalVars.C}_${sku}`;
}

function createItemCookie(asSKU, asId, anQty, anAwardQuantity) {
  createCookie(
    getCookieName(asSKU),
    `${(anQty || '1')}^1^p^${asId}^a^${(anAwardQuantity || '0')}`,
    30
  );
}

function readItemCookie(asSKU) {
  const itemCookieName = getCookieName(asSKU);

  return readCookie(itemCookieName);
}

function readQuantityOfItemCookie(itemCookie) {
  if (typeof itemCookie === 'undefined') {
    return 0;
  }

  return Number(itemCookie.split('^')[0]);
}

function removeItemCookie(asSKU) {
  deleteCookie(`ecomBag${GlobalVars.C}_${asSKU}`);
}

function setItemCookie(asSKU, asId, anQty, anAwardQuantity) {
  const existingItemCookie = readItemCookie(asSKU);
  let itemQuantity = anQty;

  if (existingItemCookie != null) {
    removeItemCookie(asSKU);

    const existingItemQuantity = readQuantityOfItemCookie(existingItemCookie);

    itemQuantity += existingItemQuantity;
  }

  createItemCookie(asSKU, asId, itemQuantity, anAwardQuantity);
}

function getItemsCount() {
  let total = 0;

  if (skusList.length > 0) {
    skusList.forEach((sku) => {
      total += readQuantityOfItemCookie(readItemCookie(sku));
    });
  }

  return total;
}

function hasItems() {
  return (skusList.length > 0);
}

function isItem(sku) {
  return (skusList.indexOf(sku) !== -1);
}

function getItemIndex(asSKU) {
  let index = skusList.length;

  while (index >= 0) {
    index -= 1;

    if (skusList[index] === asSKU) {
      break;
    }
  }

  return index;
}

function checkItemsCountBadgeDisplay() {
  if (cartItemsCountContainer.length) {
    // Show/Hide the cart items count badge
    if (hasItems()) {
      cartItemsCountContainer.removeClass(noDisplayClass);
    } else {
      cartItemsCountContainer.addClass(noDisplayClass);
    }
  }
}

function updateItemsCount() {
  // Update items count
  if (cartItemsCountContainer.length) {
    cartItemsCountContainer.text(getItemsCount());
  }

  // Check if the items count badge should be displayed
  checkItemsCountBadgeDisplay();
}

function updateSkusList(aaSkusList) {
  skusList = aaSkusList || [];

  updateItemsCount();
}

/**
 * Adds one or more items to the cart.
 * @param {Array} aaProductList                    - List of product objects.
 * @param {String} aaProductList[].sku             - SKU
 * @param {String} aaProductList[].id              - Article ID
 * @param {String} aaProductList[].title           - Title
 * @param {String} aaProductList[].price           - Price
 * @param {Number} [aaProductList[].quantity]      - Defaults to 1
 * @param {Number} [aaProductList[].awardQuantity] - Defaults to 0
 * @param {Boolean} [abNoTrigger] - Set to true for not triggering addToBag event.
 */
function addItems(aaProductList, abNoTrigger) {
  if (isEmpty(aaProductList) || (type(aaProductList) !== 'array') || !aaProductList.length) {
    return false;
  }

  const productList = [];

  for (let i = 0; i < aaProductList.length; i += 1) {
    const productItem = aaProductList[i];

    if (
      !isEmpty(productItem.sku) &&
      !isEmpty(productItem.id)
    ) {
      // Update the SKUs list if product is not already added in cart
      if (!_.includes(skusList, productItem.sku)) {
        skusList.push(productItem.sku);
      }

      // Save product data
      productList.push(productItem);

      // Create/Update cookie
      setItemCookie(
        productItem.sku,
        productItem.id,
        productItem.quantity,
        productItem.awardQuantity || 0
      );
    }
  }

  if (!productList.length) {
    return false;
  }

  if (!abNoTrigger) {
    // Trigger event
    triggerCartItemAddedEvent(productList);
  }

  return true;
}

/**
 * Adds one item to the cart.
 * @param {Object} aoProductData - Product data object.
 * @param {String} aoProductData.sku             - SKU
 * @param {String} aoProductData.id              - Article ID
 * @param {String} aoProductData.title           - Title
 * @param {String} aoProductData.price           - Price
 * @param {Number} [aoProductData.quantity]      - Defaults to 1
 * @param {Number} [aoProductData.awardQuantity] - Defaults to 0
 * @param {Boolean} [abNoTrigger] - Set to true for not triggering addToBag event.
 */
function addItem(aoProductData, abNoTrigger) {
  return addItems([aoProductData], abNoTrigger);
}

/**
 * Removes an item from the cart.
 * @param {String} asSKU   - SKU
 * @param {String} asTitle - Title for the product.
 * @param {String} asPrice - Price for the product.
 */
function removeItem(asProductId, asSKU, asTitle, asPrice, asQuantity, asCategory, asBrand, asVariant, asClass) {
  if (isEmpty(asSKU)) {
    return false;
  }

  // Update the SKUs list
  const index = getItemIndex(asSKU);
  if (index >= 0) {
    skusList.splice(index, 1);
  }

  // Remove cookie
  removeItemCookie(asSKU);

  // Trigger event
  triggerCartItemRemovedEvent([{
    productId: asProductId,
    sku: asSKU,
    title: asTitle,
    price: asPrice,
  }]);

  // Trigger event
  triggerRemoveFromCartEvent([{
    id: asSKU,
    name: asTitle,
    price: asPrice,
    quantity: asQuantity,
    category: asCategory,
    brand: asBrand,
    variant: asVariant,
    productClass: asClass
  }]);

  // if (OCMS.controls.autoReplenishment) {
  //  OCMS.controls.autoReplenishment.deleteFromCookie(asSKU);
  // } else {
  //  OCMS.loadAutoReplenishmentCode(function () {
  //    if (OCMS.controls.autoReplenishment) {
  //      OCMS.controls.autoReplenishment.deleteFromCookie(asSKU);
  //    }
  //  });
  // }

  return true;
}

function returnSkusList() {
  return skusList;
}

function init() {
  // Save references to DOM elements
  saveReferences();

  // Set event handlers
  // setEventHandlers();

  // Update items count
  updateItemsCount();

  $(document).on(cartItemAddedEventName, (event, aaProductsData) => {
    // TODO - Option dans le DOM pour ne pas afficher la popin ?
    // $(event.currentTarget).data('addtobag-popin');
    const productsQuantitySum = aaProductsData.reduce((product1, product2) => product1 + product2.quantity, 0);
    const askusList = _.map(aaProductsData, 'id');

    if (askusList.length) {
      addToBagPopin(askusList, productsQuantitySum);
    }
  });
}

// Named exports - Constants
export {
  cartItemsCountId,
};

// Named exports - Functions
export {
  init,
  getItemsCount,
  hasItems,
  updateSkusList,
  addItem,
  addItems,
  removeItem,
  returnSkusList,
  isItem,
  readItemCookie,
  readQuantityOfItemCookie,
};



// WEBPACK FOOTER //
// ./js/common/Cart.js